/**
 * ================================================================
 * ORGANIZER GUARD - Organizer Verification Protection Component
 * ================================================================
 *
 * Component bảo vệ routes yêu cầu organizer đã được verified
 *
 * Features:
 * - Kiểm tra organizer verification status từ database
 * - Redirect về Player home nếu không verified
 * - Tích hợp với OrganizerVerificationService
 * - Loading state trong quá trình kiểm tra
 *
 * @author MeBi Team
 * @version 1.0.0
 */

import React from "react";
import { Navigate } from "react-router-dom";
import { useCurrentUser } from "@/features/auth/hooks/useSession";
import { organizerService } from "@/features/organizers/services/organizer.service";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { useQuery } from "@tanstack/react-query";

interface OrganizerGuardProps {
  children: React.ReactNode;
}

const OrganizerGuard: React.FC<OrganizerGuardProps> = ({ children }) => {
  const { user, isAuthenticated, isLoading: authLoading } = useCurrentUser();
  const currentUserId = user?.id ?? null;

  const { data: orgStatus, isLoading: isVerifying } = useQuery({
    queryKey: ["organizerVerification", currentUserId],
    queryFn: () => organizerService.getVerificationStatus(),
    enabled: !!isAuthenticated && !!currentUserId,
    staleTime: 5 * 60 * 1000,
  });

  const isVerified = orgStatus?.status === "verified";

  // Show loading while auth is loading or verifying organizer status
  if (authLoading || (isAuthenticated && isVerifying)) {
    return <LoadingSpinner text="Đang kiểm tra quyền truy cập..." />;
  }

  // Redirect to login if no user
  if (!isAuthenticated || !currentUserId) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to Player home if not verified organizer
  if (!isVerified) {
    return (
      <Navigate
        to="/tabs/home"
        replace
        state={{
          message: "Bạn cần được xác minh tổ chức để truy cập tính năng này.",
          type: "warning",
        }}
      />
    );
  }

  // Allow access for verified organizers
  return <>{children}</>;
};

export default OrganizerGuard;
